import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  Form,
  TextInput,
  regex,
  required,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { AdminDataProvider } from '../dataProvider';

/**
 * Blog-category create (D-cms-blog). A category is key / label / description, the
 * storeTags vocabulary shape. The KEY is the stable ref a post points at, so it is set
 * once here and immutable thereafter (the edit shows it read-only). New categories get
 * an `action='create'` audit entry through the #25 wrapper; there is no audit code here.
 * Gated cms:author (editor and up); a viewer is denied at the route.
 */

const keyValidators = [
  required('Key is required'),
  regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    'Key must be lowercase words joined by hyphens',
  ),
];

interface BlogCategoryFormValues {
  key: string;
  label: string;
  description: string;
}

/** The create view. Exported for testing without the access wrapper. */
export const BlogCategoryCreateView = () => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as BlogCategoryFormValues;
    try {
      // Guard the stable key's uniqueness (it is the post's category ref).
      const existing = await dataProvider.getList('blogCategories', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: { key: values.key },
      });
      if (existing.total && existing.total > 0) {
        notify('That key is already in use', { type: 'error' });
        return;
      }
      const { data } = await dataProvider.create('blogCategories', {
        data: {
          key: values.key,
          label: values.label,
          description: values.description,
        },
      });
      notify('Category created', { type: 'success' });
      redirect('edit', 'blogCategories', data.id);
    } catch {
      notify('Could not create the category', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        New blog category
      </Typography>
      <Form onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextInput
              source="key"
              label="Key"
              validate={keyValidators}
              helperText="The stable reference, e.g. brand-spotlight. Cannot be changed later."
              fullWidth
            />
            <TextInput
              source="label"
              label="Label"
              validate={required('Label is required')}
              fullWidth
            />
            <TextInput
              source="description"
              label="Description"
              validate={required('Description is required')}
              multiline
              fullWidth
            />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Create
          </Button>
          <Button
            type="button"
            onClick={() => redirect('list', 'blogCategories')}
          >
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** Gated entry point registered as the `blogCategories` Resource's create. */
export const BlogCategoryCreate = () => (
  <RequireAccess capability="cms:author">
    <BlogCategoryCreateView />
  </RequireAccess>
);
